<script lang="ts" setup>
import { computed, inject, ref } from 'vue'
import type { Ref } from 'vue'
import type { ScrollChapter } from '../composables/useInfiniteChapter'
import type { ChapterConfig } from '@/composables/payment'
import { PaymentInlinePanel } from '@/composables/payment'
import { CHARGE_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'unlock'): void
  (e: 'pay-success'): void
}>()

interface Props {
  chapter: ScrollChapter
  memberCoin: number
  contentId: string
  bookName?: string
  serialStatus?: number
}

const config = inject<ChapterConfig>('chapterConfig')!
const theme = inject<Ref<{ background: string; size: string }>>('theme')
const autoLock = ref(true)
// 无充值档位且订阅全为畅读会员时由 PaymentInlinePanel 上报
const membershipOnly = ref(false)
const { t } = useI18n()

const themeBackground = computed(() => theme?.value?.background ?? '#F6F7F9')
const balanceEnough = computed(() => props.memberCoin >= props.chapter.priceAmount)

// 夜间皮肤判定：按主题背景色亮度，低亮度即深色（用于支付方式选择器的暗色样式/白色图标）
// 与分页模式 ChapterUnlock 保持一致，避免滚动模式漏传 dark 导致暗色下图标仍为彩色
const isDarkTheme = computed(() => {
  const bg = themeBackground.value
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(bg || '')
  if (!m)
    return false
  const r = Number.parseInt(m[1], 16)
  const g = Number.parseInt(m[2], 16)
  const b = Number.parseInt(m[3], 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) < 80
})

function handleUnlock() {
  emit('unlock')
}

function toggleAutoLock() {
  autoLock.value = !autoLock.value
}

function handlePaySuccess() {
  emit('pay-success')
}

defineExpose({
  autoLock,
})
</script>

<template>
  <div class="chapter-unlock relative pt-40px text-center">
    <!-- 渐变遮罩覆盖上方内容 -->
    <div class="mask" />

    <!-- 解锁区域 -->
    <div class="unlock-content">
      <!-- 装饰分割线 + 锁图标 -->
      <div class="unlock-divider flex items-center justify-center mb-16px">
        <span class="unlock-divider-line" />
        <span class="unlock-divider-icon mx-8px" aria-hidden="true" />
        <span class="unlock-divider-line" />
      </div>

      <!-- 仅订阅(畅读会员)场景: 统一文案, 不展示金币解锁相关 -->
      <template v-if="membershipOnly">
        <h3 class="theme-h3 font-bold text-18px text-center">
          {{ config.texts.unlockTitle }}
        </h3>
        <p class="theme-desc mt-8px lh-24px text-14px text-center" style="white-space: pre-line;">
          {{ t('UnlockMembership') }}
        </p>
      </template>
      <template v-else>
        <h3 class="theme-h3 font-bold text-18px text-center">
          {{ config.texts.unlockTitle }}
        </h3>
        <p class="theme-desc mt-8px lh-24px text-14px text-center" style="white-space: pre-line;">
          <template v-if="chapter.feeType === CHARGE_TYPE.CHAPTER">
            {{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}
          </template>
          <template v-else>
            {{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}
          </template>
        </p>
      </template>

      <!-- 余额足够：Unlock 按钮 -->
      <template v-if="balanceEnough && !membershipOnly">
        <a
          class="theme-btn inline-block mt-24px px-24px py-3 min-h-40px lh-1em text-15px text-white text-center btn-solid rounded-full w-300px"
          @click.stop="handleUnlock"
        >
          <span v-if="chapter.priceAmount !== undefined">
            <span v-if="chapter.feeType === CHARGE_TYPE.CHAPTER">{{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}</span>
            <span v-else>{{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}</span>
          </span>
        </a>
      </template>

      <!-- 余额不足：常驻支付版位 -->
      <template v-else>
        <div class="mt-24px" :class="{ 'pb-18px': membershipOnly }">
          <PaymentInlinePanel
            :content-id="contentId"
            :content-chapter-id="chapter.id"
            :book-name="bookName || ''"
            :chapter-name="chapter.name"
            :serial-status="serialStatus || 0"
            :dark="isDarkTheme"
            @success="handlePaySuccess"
            @update:membership-only="membershipOnly = $event"
          />
        </div>
      </template>

      <!-- Auto unlock 开关(仅订阅场景隐藏) -->
      <div v-if="!membershipOnly" class="theme-radio mt-24px flex justify-center items-center" @click.stop="toggleAutoLock">
        <div class="radio auto-lock-radio flex justify-center items-center w-14px h-14px inline-block border b-rd-14px cursor" :class="{ selected: autoLock }">
          <span class="inline-block w-6px h-6px bg-n-primary b-rd-6px" />
        </div>
        <span class="ml-6px label theme-desc text-14px">{{ config.texts.autoUnlock }}</span>
      </div>

      <!-- 余额展示(仅订阅场景隐藏) -->
      <p v-if="!membershipOnly" class="theme-balance mt-12px pb-18px lh-24px text-14px text-center">
        {{ t('NovelBalance', { 0: memberCoin }) }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.radio {
  span {
    display: none;
  }

  &.selected {
    border-color: var(--primary-color);

    span {
      display: inline-block
    }
  }
}

.auto-lock-radio {
  border-color: var(--theme-balance-color, #9296A0);
}

.chapter-unlock {
  position: relative;

  .mask {
    position: absolute;
    top: -126px;
    left: 0;
    width: 100%;
    height: 126px;
    background: var(--mask-gradient, linear-gradient(180deg, rgba(246,247,249,0.00) 20%, var(--theme-background, #F6F7F9) 100%));
    pointer-events: none;
  }

  .unlock-content {
    position: relative;
    z-index: 1;
  }
}

.unlock-divider {
  width: 100%;
}

.unlock-divider-line {
  width: 80px;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--theme-balance-color, rgba(146, 150, 160, 0.4)), transparent);
  opacity: 0.5;
}

.unlock-divider-icon {
  display: inline-block;
  width: 16px;
  height: 18px;
  flex-shrink: 0;
  background-color: var(--theme-balance-color, #9296A0);
  -webkit-mask-image: url('/images/chapter-lock.svg');
  mask-image: url('/images/chapter-lock.svg');
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}
</style>
